import styled, { css } from 'styled-components';

export const Wrapper = styled.div<{ $compact?: boolean }>`
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
  width: ${({ $compact }) => ($compact ? 'auto' : '100%')};
  min-width: ${({ $compact }) => ($compact ? '9.5rem' : '0')};
  flex: ${({ $compact }) => ($compact ? '0 0 auto' : 'initial')};
`;

export const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: ${({ theme }) => theme.dark};
  transition: color 0.3s ease;
`;

export const SelectControl = styled.div<{ $compact?: boolean }>`
  position: relative;
  width: ${({ $compact }) => ($compact ? 'auto' : '100%')};
  min-width: ${({ $compact }) => ($compact ? '9.5rem' : '0')};
`;

export const TriggerButton = styled.button<{
  $open?: boolean;
  $disabled?: boolean;
  $compact?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  height: ${({ $compact }) => ($compact ? '2.81rem' : '2.8rem')};
  min-width: ${({ $compact }) => ($compact ? '9.5rem' : '0')};
  border-radius: 0.5rem;
  border: 1px solid ${({ theme, $open }) => ($open ? theme.primary : theme.grey)};
  padding: 0 0.85rem 0 1rem;
  outline: none;
  font-size: 0.875rem;
  font-family: inherit;
  color: ${({ theme }) => theme.dark};
  background-color: ${({ theme, $disabled }) => ($disabled ? theme.lightGrey : theme.background)};
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    background-color 0.2s ease;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  box-shadow: ${({ theme, $open }) => ($open ? `0 0 0 3px ${theme.lightGrey}` : 'none')};

  &:hover:not(:disabled) {
    border-color: ${({ theme, $open }) => ($open ? theme.primary : theme.midGrey)};
  }

  &:focus-visible {
    border-color: ${({ theme }) => theme.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.lightGrey};
  }

  &:disabled {
    border-color: ${({ theme }) => theme.grey};
    color: ${({ theme }) => theme.midGrey};
    opacity: 0.6;
  }
`;

export const TriggerValue = styled.span<{ $placeholder?: boolean }>`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
  color: ${({ theme, $placeholder }) => ($placeholder ? theme.midGrey : theme.dark)};
`;

export const ChevronIcon = styled.span<{ $open?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ theme }) => theme.midGrey};
  transition: transform 0.2s ease;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

export const Dropdown = styled.ul<{ $compact?: boolean }>`
  position: absolute;
  top: calc(100% + 0.35rem);
  /* A compact trigger is deliberately narrow, so the menu sizes to its longest
     option instead of inheriting that width and ellipsing every label. Compact
     selects sit in right-aligned toolbars, so it grows leftwards to stay on
     screen. */
  ${({ $compact }) =>
    $compact
      ? `left: auto;
  right: 0;
  width: max-content;
  min-width: 100%;
  max-width: min(22rem, 80vw);`
      : `left: 0;
  right: 0;`}
  z-index: 120;
  margin: 0;
  padding: 0.35rem;
  list-style: none;
  border: 1px solid ${({ theme }) => theme.grey};
  border-radius: 0.625rem;
  background: ${({ theme }) => theme.background};
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  max-height: 15rem;
  overflow-y: auto;
  overscroll-behavior: contain;
`;

export const OptionButton = styled.button<{
  $selected?: boolean;
  $highlighted?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  min-height: 2.5rem;
  padding: 0.55rem 0.75rem;
  border: none;
  border-radius: 0.45rem;
  background: transparent;
  font-size: 0.875rem;
  font-family: inherit;
  text-align: left;
  color: ${({ theme }) => theme.dark};
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  ${({ theme, $selected, $highlighted }) => {
    if ($selected) {
      return css`
        background: ${theme.primary90 || theme.lightGrey};
        color: ${theme.primary};
        font-weight: 600;
      `;
    }

    if ($highlighted) {
      return css`
        background: ${theme.lightGrey};
      `;
    }

    return '';
  }}

  &:hover {
    background: ${({ theme, $selected }) => ($selected ? theme.primary90 || theme.lightGrey : theme.lightGrey)};
  }
`;

export const OptionLabel = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CheckIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ theme }) => theme.primary};
`;

export const EmptyState = styled.li`
  padding: 0.75rem;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.midGrey};
  text-align: center;
`;
